import React, { useState, useEffect, useRef } from 'react';
import { LogOut, Plus, Check, BarChart3, Users, Clock, Upload, Download, AlertCircle, CheckCircle, WifiOff, Trophy, Trash2, Save, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

// '??' (non '||'): una REACT_APP_API_URL vuota è intenzionale (stesso-origine,
// dietro un reverse proxy che instrada /api e /health al backend) e va
// rispettata, non scambiata per "non impostata".
const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5000';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';
const APP_VERSION_LABEL = APP_VERSION.startsWith('v') ? APP_VERSION : `build ${APP_VERSION}`;

// URL del pannello admin per il link nell'header (visibile solo agli admin). REACT_APP_ADMIN_URL, se
// impostata a build time, ha la precedenza; altrimenti si ricava dall'host corrente: app.<dominio> ->
// admin.<dominio> (produzione), localhost/127.0.0.1:3001 -> :3002 (sviluppo). In tutti gli altri casi
// null: il link resta nascosto, meglio che indovinare un indirizzo sbagliato.
function getAdminUrl() {
  const configured = (process.env.REACT_APP_ADMIN_URL || '').trim();
  if (configured) return configured;
  if (typeof window === 'undefined') return null;
  const { protocol, hostname, port } = window.location;
  if (hostname.startsWith('app.')) {
    return `${protocol}//admin.${hostname.slice(4)}${port ? `:${port}` : ''}`;
  }
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '3001') {
    return `${protocol}//${hostname}:3002`;
  }
  return null;
}
const ADMIN_URL = getAdminUrl();

// Le categorie atleta sono gestite (CRUD) nel pannello admin dedicato (vedi admin/) e caricate qui
// in sola lettura da GET /api/categories via lo stato `categories`. Il nome della categoria è già
// l'etichetta da mostrare, quindi non serve una mappa valore->etichetta separata.

// Taglie maglietta ammesse per `athletes.taglia`, in ordine crescente. Lista duplicata nel backend
// (validazione) e in admin/: se cambia, aggiornarla in tutti e tre i punti.
const TAGLIE = ['S', 'M', 'L', 'XL', 'XXL'];

// I giorni di corso ("turni") sono gestiti allo stesso modo nel pannello admin e caricati qui in
// sola lettura da GET /api/course-days via lo stato `courseDays`.

// Mappa il giorno della settimana JS (Date.getDay(): 0=domenica...6=sabato) al nome del giorno di
// corso corrispondente, per il riconoscimento automatico dalla data selezionata in fase di appello.
const WEEKDAY_TO_DAY_NAME = {
  0: 'domenica',
  1: 'lunedì',
  2: 'martedì',
  3: 'mercoledì',
  4: 'giovedì',
  5: 'venerdì',
  6: 'sabato'
};

// Giorni di corso che un utente può selezionare in fase di appello: un admin, o un istruttore a cui
// non è stato ancora assegnato nessun turno, vede tutti i giorni esistenti (nessun blocco per gli
// account non configurati). `allDayNames` è l'elenco corrente dei turni (stato `courseDays`).
function getAllowedCourseDays(u, allDayNames) {
  if (!u || u.role === 'admin' || !u.turni || u.turni.length === 0) {
    return allDayNames;
  }
  return u.turni;
}

// Impianti selezionabili in appello: un admin, o un istruttore senza impianti assegnati, li vede tutti
// (con in più l'opzione "Tutti gli impianti"); altrimenti solo quelli in cui lavora.
function canSeeAllImpianti(u) {
  return !u || u.role === 'admin' || !u.impianti || u.impianti.length === 0;
}
function getAllowedImpianti(u, allImpiantoNames) {
  return canSeeAllImpianti(u) ? allImpiantoNames : u.impianti;
}

// Ultimo impianto scelto in appello, ricordato solo nel browser (comodità per istruttore).
const IMPIANTO_STORAGE_KEY = 'appelloImpianto';
function readStoredImpianto() {
  try {
    return localStorage.getItem(IMPIANTO_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}
function storeImpianto(value) {
  try {
    localStorage.setItem(IMPIANTO_STORAGE_KEY, value);
  } catch {
    // storage non disponibile: la scelta vale solo per questa sessione
  }
}

// Data odierna in formato YYYY-MM-DD secondo il fuso orario locale (non UTC, che a inizio giornata
// in Italia darebbe ancora il giorno precedente).
function localISODate(d = new Date()) {
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Assenze di un atleta raggruppate per giorno della settimana (tab Statistiche): [{ day, count }].
// I giorni elencati sono quelli di allenamento della sua categoria (anche con 0 assenze), oppure, se la
// categoria non ha turni configurati, quelli in cui risultano assenze. Ordine di calendario (`dayOrder`
// = nomi di `courseDays`, già ordinati per id), non alfabetico.
function absencesByWeekday(absentDates, categoryDays, dayOrder) {
  const counts = {};
  for (const a of absentDates || []) {
    counts[a.course_day] = (counts[a.course_day] || 0) + 1;
  }
  const days = categoryDays && categoryDays.length > 0 ? [...categoryDays] : Object.keys(counts);
  const rank = d => (dayOrder.indexOf(d) === -1 ? dayOrder.length : dayOrder.indexOf(d));
  return days
    .sort((x, y) => rank(x) - rank(y))
    .map(day => ({ day, count: counts[day] || 0 }));
}

// Riga compatta "Assenze <mese>: N — 2 set, 9 set" usata nelle tab Atleti e Appello, stesso formato
// data della tab Statistiche. `absentDates` undefined = dati del mese non ancora caricati.
function AbsenceSummary({ absentDates, month, darkMode }) {
  if (!absentDates) return null;
  const monthName = new Date(`${month}-01T00:00:00`).toLocaleDateString('it-IT', { month: 'long' });
  if (absentDates.length === 0) {
    return <p className="text-xs mt-1 opacity-60">Nessuna assenza a {monthName}</p>;
  }
  return (
    <p className={`text-xs mt-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
      Assenze {monthName}: {absentDates.length} —{' '}
      {absentDates
        .map(a => new Date(`${a.date}T00:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }))
        .join(', ')}
    </p>
  );
}

// Differenza in giorni (-2..+2) tra il compleanno dell'atleta e la data dell'appello (`dateStr`,
// YYYY-MM-DD): positivo = compleanno nei giorni successivi, negativo = già passato. `null` se fuori
// finestra o senza data di nascita. Lavora solo sulle componenti numeriche in UTC (niente slittamenti di
// fuso), confronta il compleanno nell'anno precedente/corrente/successivo (cambio d'anno) e per i nati
// il 29 febbraio usa il 28 negli anni non bisestili.
function birthdayOffset(dataNascita, dateStr) {
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(dataNascita || '');
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr || '');
  if (!b || !d) return null;
  const [bYear, bMonth, bDay] = [Number(b[1]), Number(b[2]), Number(b[3])];
  const year = Number(d[1]);
  const ref = Date.UTC(year, Number(d[2]) - 1, Number(d[3]));
  const isLeap = y => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  for (const y of [year - 1, year, year + 1]) {
    if (y <= bYear) continue; // nessun compleanno prima di aver compiuto 1 anno
    const day = bMonth === 2 && bDay === 29 && !isLeap(y) ? 28 : bDay;
    const diff = Math.round((Date.UTC(y, bMonth - 1, day) - ref) / 86400000);
    if (Math.abs(diff) <= 2) return diff;
  }
  return null;
}

// Testo del badge compleanno nell'Appello; N = anni compiuti nel compleanno di riferimento.
function birthdayLabel(dataNascita, dateStr, offset) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const bday = new Date(Date.UTC(y, m - 1, d + offset));
  const age = bday.getUTCFullYear() - Number(dataNascita.slice(0, 4));
  switch (offset) {
    case 0: return `🎂 Compie ${age} anni oggi!`;
    case 1: return `🎂 Compleanno domani (${age} anni)`;
    case 2: return `🎂 Compleanno tra 2 giorni (${age} anni)`;
    case -1: return `🎂 Compleanno ieri (${age} anni)`;
    default: return `🎂 Compleanno 2 giorni fa (${age} anni)`;
  }
}

// Parola breve per il banner riepilogativo dei compleanni in Appello.
const BIRTHDAY_WHEN = { '-2': '2 giorni fa', '-1': 'ieri', '0': 'oggi', '1': 'domani', '2': 'tra 2 giorni' };

// Tempi gara: duplicato da parseTempo/formatTempo in backend/src/server.js (la validazione vera resta
// lato server, qui serve solo a dare un riscontro immediato; va mostrato comunque l'errore 400 del backend).
function formatTempo(cs) {
  if (cs === null || cs === undefined || cs === '') return '';
  const n = Math.round(Number(cs));
  if (!Number.isFinite(n) || n < 0) return '';
  const min = Math.floor(n / 6000);
  const sec = Math.floor((n % 6000) / 100);
  const cent = n % 100;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')},${String(cent).padStart(2, '0')}`;
}

function isValidTempo(str) {
  if (typeof str !== 'string') return false;
  const s = str.replace(/'/g, ':').replace(/"/g, ',').replace(/\s+/g, '');
  const m = s.match(/^(?:(\d{1,3}):)?(\d{1,2})(?:[.,](\d{1,2}))?$/);
  if (!m) return false;
  if (m[1] !== undefined && Number(m[2]) >= 60) return false;
  return true;
}

const STATI_GARA = { SQ: 'Squalificato', RT: 'Ritirato', NP: 'Non partito' };

export default function SwimmingAttendanceApp() {
  const [user, setUser] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [activeTab, setActiveTab] = useState('attendance'); // attendance | athletes | stats | races
  const [searchTerm, setSearchTerm] = useState('');
  const [todayAttendance, setTodayAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [statistics, setStatistics] = useState(null);
  // Assenze per mese e per atleta ({ 'YYYY-MM': { [athleteId]: absent_dates } }) mostrate nelle tab
  // Atleti (mese corrente) e Appello (mese della data selezionata), da GET /api/statistics/month.
  const [absencesByMonth, setAbsencesByMonth] = useState({});
  const [editingAthlete, setEditingAthlete] = useState(null); // null = chiuso, {} = nuovo, {id,...} = modifica
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Categorie e turni sono gestiti in sola lettura qui (servono al form atleti, alle checkbox
  // categorie/turni istruttore e alla selezione giorno in appello): la relativa gestione CRUD è
  // stata spostata nel pannello di amministrazione dedicato (vedi admin/), riservato agli admin.
  const [categories, setCategories] = useState([]);
  const [courseDays, setCourseDays] = useState([]);
  // Impianti (sola lettura, CRUD in admin/) e impianto scelto in appello ('' = tutti)
  const [impianti, setImpianti] = useState([]);
  const [selectedImpianto, setSelectedImpianto] = useState(readStoredImpianto);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch athletes, categories and course days on auth (servono al form atleti, alle checkbox
  // categorie/turni istruttore e alla selezione giorno in appello, tutte raggiungibili subito dopo il login)
  useEffect(() => {
    if (user) {
      fetchAthletes();
      fetchCategories();
      fetchCourseDays();
      fetchImpianti();
    }
  }, [user]);

  // Quando cambia la data dell'appello, deriva il giorno di corso e carica le presenze già registrate.
  // Se il giorno derivato non rientra nei turni dell'istruttore (quando ne ha almeno uno assegnato),
  // non viene pre-selezionato: dovrà scegliere manualmente uno dei giorni in cui lavora davvero.
  useEffect(() => {
    if (!user) return;
    const weekday = new Date(`${selectedDate}T00:00:00`).getDay();
    const derivedDay = WEEKDAY_TO_DAY_NAME[weekday] || '';
    const allDayNames = courseDays.map(d => d.nome);
    const courseDay = getAllowedCourseDays(user, allDayNames).includes(derivedDay) ? derivedDay : '';
    applyAppelloSelection(courseDay, selectedDate);
  }, [user, selectedDate, courseDays]);

  // Carica le assenze del mese corrente (tab Atleti) e del mese della data d'appello (tab Appello),
  // così sono già disponibili senza passare dalla tab Statistiche.
  useEffect(() => {
    if (!user) return;
    const months = new Set([localISODate().slice(0, 7), selectedDate.slice(0, 7)]);
    months.forEach(m => fetchMonthAbsences(m));
  }, [user, selectedDate]);

  // Ricarica le statistiche quando cambia il mese, se la tab è attiva
  useEffect(() => {
    if (user && activeTab === 'stats') {
      fetchStatistics(selectedMonth);
    }
  }, [selectedMonth]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem('token');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential })
      });

      if (response.ok) {
        const { token, user: userData } = await response.json();
        localStorage.setItem('token', token);
        setUser(userData);
      } else {
        const body = await response.json().catch(() => ({}));
        alert(body.error || 'Accesso con Google non autorizzato');
      }
    } catch (err) {
      alert('Errore login con Google: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
      }
    } catch (err) {
      console.error('Failed to fetch athletes:', err);
    }
  };

  const saveAthlete = async (athleteData) => {
    try {
      const token = localStorage.getItem('token');
      const isEditing = Boolean(athleteData.id);
      const response = await fetch(
        `${API_URL}/api/athletes${isEditing ? `/${athleteData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(athleteData)
        }
      );

      if (response.ok) {
        await fetchAthletes();
        setEditingAthlete(null);
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Errore salvataggio atleta: ' + (err.error || response.statusText));
      }
    } catch (err) {
      alert('Errore salvataggio atleta: ' + err.message);
    }
  };

  const deleteAthlete = async (athleteId) => {
    if (!window.confirm('Eliminare questo atleta? L\'operazione non è reversibile.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes/${athleteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchAthletes();
      } else {
        alert('Errore eliminazione atleta');
      }
    } catch (err) {
      alert('Errore eliminazione atleta: ' + err.message);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchCourseDays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/course-days`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCourseDays(data);
      }
    } catch (err) {
      console.error('Failed to fetch course days:', err);
    }
  };

  const fetchImpianti = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/impianti`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setImpianti(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch impianti:', err);
    }
  };

  const changeImpianto = (value) => {
    setSelectedImpianto(value);
    storeImpianto(value);
  };

  const importAthletesFile = async (file) => {
    if (!file) return;
    try {
      setImporting(true);
      setImportResult(null);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/athletes/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setImportResult(data);
        await fetchAthletes();
      } else {
        alert('Errore importazione: ' + (data.error || response.statusText));
      }
    } catch (err) {
      alert('Errore importazione: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const downloadAthletesTemplate = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        alert('Errore scaricamento modello: ' + (err.error || response.statusText));
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'atleti.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore scaricamento modello: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const fetchAttendanceForSelection = async (day, date) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        course_day: day,
        from_date: date,
        to_date: date
      });
      const response = await fetch(`${API_URL}/api/attendance?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const records = await response.json();
        const map = {};
        records.forEach(r => { map[r.athlete_id] = r.present; });
        setTodayAttendance(map);
      }
    } catch (err) {
      console.error('Failed to fetch attendance:', err);
    }
  };

  const applyAppelloSelection = async (day, date) => {
    setSelectedDay(day);
    if (day && date) {
      await fetchAttendanceForSelection(day, date);
    } else {
      setTodayAttendance({});
    }
  };

  const fetchStatistics = async (month) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/statistics/month?month=${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Stessa sorgente della tab Statistiche, ma senza toccare `loading`/`statistics`: qui servono solo
  // le date di assenza per atleta, da mostrare nelle tab Atleti e Appello.
  const fetchMonthAbsences = async (month) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/statistics/month?month=${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const byAthlete = {};
        for (const stat of data.athletes || []) {
          byAthlete[stat.athlete_id] = stat.absent_dates || [];
        }
        setAbsencesByMonth(prev => ({ ...prev, [month]: byAthlete }));
      }
    } catch (err) {
      console.error('Failed to fetch month absences:', err);
    }
  };

  // L'istruttore inserisce solo la presenza: chi non viene marcato per quella data/turno è
  // considerato assente per definizione, non viene salvata alcuna riga di assenza esplicita.
  const toggleAttendance = async (athleteId) => {
    const present = todayAttendance[athleteId] !== true;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          athlete_id: athleteId,
          course_day: selectedDay,
          date: selectedDate,
          present,
          instructor_id: user.id,
          // '' (tutti gli impianti) -> il backend usa l'impianto dell'atleta
          impianto: effectiveImpianto || null
        })
      });

      setTodayAttendance({
        ...todayAttendance,
        [athleteId]: present
      });
      // Marcare/smarcare una presenza cambia le assenze del mese mostrate nelle tab Appello/Atleti
      fetchMonthAbsences(selectedDate.slice(0, 7));
    } catch (err) {
      console.error('Failed to mark attendance:', err);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setAthletes([]);
    setAbsencesByMonth({});
  };

  // Impianto effettivo dell'appello: la scelta salvata se ancora ammessa, altrimenti "tutti" per chi
  // può vederli tutti, o il primo impianto dell'istruttore.
  const allowedImpianti = getAllowedImpianti(user, impianti.map(i => i.nome));
  const effectiveImpianto = allowedImpianti.includes(selectedImpianto)
    ? selectedImpianto
    : (canSeeAllImpianti(user) ? '' : (allowedImpianti[0] || ''));

  // ========== RENDER ==========

  if (!user) {
    return (
      <LoginScreen
        onGoogleLogin={loginWithGoogle}
        googleClientId={GOOGLE_CLIENT_ID}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />
    );
  }

  // In fase di appello un istruttore vede solo gli atleti delle categorie che gestisce (se gliene è
  // stata assegnata almeno una) E dell'impianto selezionato (filtri in AND); un admin, o un
  // istruttore senza categorie assegnate, vede tutte le categorie. Con "Tutti gli impianti"
  // (solo admin o istruttore senza impianti) non si filtra per impianto.
  const appelloAthletes = athletes
    .filter(a => user.role === 'admin' || !user.categorie || user.categorie.length === 0 || user.categorie.includes(a.categoria))
    .filter(a => !effectiveImpianto || (a.impianti || []).includes(effectiveImpianto));

  const filteredAthletes = appelloAthletes.filter(a =>
    `${a.nome} ${a.cognome}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const appelloMonth = selectedDate.slice(0, 7);
  // Compleanni (±2 giorni dalla data dell'appello, non da oggi) tra gli atleti visibili in Appello
  const appelloBirthdays = filteredAthletes
    .map(a => ({ athlete: a, offset: birthdayOffset(a.data_nascita, selectedDate) }))
    .filter(b => b.offset !== null)
    .sort((x, y) => Math.abs(x.offset) - Math.abs(y.offset));
  const birthdayOffsetById = Object.fromEntries(appelloBirthdays.map(b => [b.athlete.id, b.offset]));
  const currentMonth = localISODate().slice(0, 7);
  const athleteAbsences = (month, athleteId) => {
    const byAthlete = absencesByMonth[month];
    // Il backend mette in absent_dates solo le sessioni fino a ieri, quindi qui non serve filtrare le date future
    return byAthlete ? (byAthlete[athleteId] || []) : undefined;
  };

  const statsSummary = statistics?.athletes?.length
    ? (() => {
        const athletes = statistics.athletes;
        const totalSessions = athletes.reduce((sum, athlete) => sum + (athlete.total_days || 0), 0);
        const totalPresences = athletes.reduce((sum, athlete) => sum + (athlete.present_days || 0), 0);
        const averagePresence = Math.round(
          athletes.reduce((sum, athlete) => sum + (athlete.percentage ?? 0), 0) / athletes.length
        );
        const athletesPresent = athletes.filter(athlete => (athlete.present_days || 0) > 0).length;
        const athletesMissing = athletes.length - athletesPresent;
        const totalAbsences = athletes.reduce((sum, athlete) => sum + (athlete.absent_dates?.length || 0), 0);
        const aboveTarget = athletes.filter(athlete => (athlete.percentage ?? 0) >= 80).length;
        const belowHalf = athletes.filter(athlete => (athlete.percentage ?? 0) < 50 && (athlete.total_days || 0) > 0).length;

        return {
          totalSessions,
          totalPresences,
          averagePresence,
          athletesPresent,
          athletesMissing,
          totalAbsences,
          aboveTarget,
          belowHalf,
          attendanceRate: totalSessions > 0 ? Math.round((totalPresences / totalSessions) * 100) : 0
        };
      })()
    : null;

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900 text-white' : 'bg-gray-50 text-slate-900'} pb-24`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-lg sticky top-0 z-40`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">
              🏊 Appello Nuoto Web{' '}
              <span className={`text-xs font-normal align-middle ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                {APP_VERSION_LABEL}
              </span>
            </h1>
            <p className="text-xs opacity-60">{user.name}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Scorciatoia verso il pannello admin: il ruolo lo verifica comunque l'app admin stessa */}
            {user.role === 'admin' && ADMIN_URL && (
              <a
                href={ADMIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                title="Apri il pannello admin in una nuova scheda"
                className={`px-3 py-2 rounded-lg transition flex items-center gap-1.5 text-sm font-medium ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
              >
                <Shield size={16} />
                Admin
              </a>
            )}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`px-3 py-2 rounded-lg transition ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`sticky top-14 z-30 ${darkMode ? 'bg-slate-800' : 'bg-white'} border-b ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
        <div className="flex gap-0 max-w-4xl mx-auto">
          <TabButton
            active={activeTab === 'attendance'}
            onClick={() => setActiveTab('attendance')}
            icon={<Clock size={18} />}
            label="Appello"
            darkMode={darkMode}
          />
          <TabButton
            active={activeTab === 'athletes'}
            onClick={() => setActiveTab('athletes')}
            icon={<Users size={18} />}
            label="Atleti"
            darkMode={darkMode}
          />
          <TabButton
            active={activeTab === 'stats'}
            onClick={() => { setActiveTab('stats'); fetchStatistics(selectedMonth); }}
            icon={<BarChart3 size={18} />}
            label="Statistiche"
            darkMode={darkMode}
          />
          <TabButton
            active={activeTab === 'races'}
            onClick={() => setActiveTab('races')}
            icon={<Trophy size={18} />}
            label="Gare"
            darkMode={darkMode}
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div>
            <div className="mb-4 space-y-2">
              <div>
                <label className="block text-xs opacity-60 mb-1">Data appello</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-200 border-gray-300'}`}
                />
              </div>

              {allowedImpianti.length > 1 ? (
                <div>
                  <label className="block text-xs opacity-60 mb-1">Impianto</label>
                  <select
                    value={effectiveImpianto}
                    onChange={(e) => changeImpianto(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
                  >
                    {canSeeAllImpianti(user) && <option value="">Tutti gli impianti</option>}
                    {allowedImpianti.map(nome => (
                      <option key={nome} value={nome}>{nome}</option>
                    ))}
                  </select>
                </div>
              ) : effectiveImpianto && (
                <div className={`px-3 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                  Impianto: {effectiveImpianto}
                </div>
              )}

              {selectedDay ? (
                <div className={`px-3 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                  Giorno di corso: {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}
                </div>
              ) : (
                <div className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                  La data selezionata non corrisponde a uno dei tuoi giorni di corso. Scegli manualmente il gruppo per registrare comunque l'appello.
                </div>
              )}

              <select
                value={selectedDay}
                onChange={(e) => applyAppelloSelection(e.target.value, selectedDate)}
                className={`w-full px-4 py-2 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
              >
                <option value="">-- Seleziona gruppo --</option>
                {courseDays
                  .filter(d => getAllowedCourseDays(user, courseDays.map(cd => cd.nome)).includes(d.nome))
                  .map(d => (
                    <option key={d.id} value={d.nome}>{d.nome.charAt(0).toUpperCase() + d.nome.slice(1)}</option>
                  ))}
              </select>
            </div>

            <div className="mb-4">
              <input
                type="text"
                placeholder="Ricerca atleta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg text-lg ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-200 border-gray-300'} border focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            {appelloBirthdays.length > 0 && (
              <div className={`mb-3 px-3 py-2 rounded-lg text-sm font-medium border ${darkMode ? 'bg-pink-900/40 text-pink-200 border-pink-700' : 'bg-pink-100 text-pink-800 border-pink-300'}`}>
                🎂 Compleanni:{' '}
                {appelloBirthdays
                  .map(b => `${b.athlete.nome} ${b.athlete.cognome} (${BIRTHDAY_WHEN[b.offset]})`)
                  .join(', ')}
              </div>
            )}

            <div className="space-y-2">
              {filteredAthletes.length === 0 ? (
                <p className="text-center py-8 opacity-50">Nessun atleta trovato</p>
              ) : (
                filteredAthletes.map(athlete => (
                  <div
                    key={athlete.id}
                    className={`p-4 rounded-lg flex items-center gap-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} ${
                      birthdayOffsetById[athlete.id] !== undefined ? 'ring-2 ring-pink-400' : ''
                    }`}
                  >
                    {athlete.photo_url && (
                      <img
                        src={athlete.photo_url}
                        alt={`${athlete.nome} ${athlete.cognome}`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold">{athlete.nome} {athlete.cognome}</p>
                      {birthdayOffsetById[athlete.id] !== undefined && (
                        <span className={`inline-block my-1 text-sm font-bold px-2 py-1 rounded-lg ${
                          birthdayOffsetById[athlete.id] === 0
                            ? 'bg-pink-500 text-white'
                            : darkMode ? 'bg-pink-900/60 text-pink-200' : 'bg-pink-100 text-pink-800'
                        }`}>
                          {birthdayLabel(athlete.data_nascita, selectedDate, birthdayOffsetById[athlete.id])}
                        </span>
                      )}
                      <p className="text-xs opacity-50">{athlete.email}</p>
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                        {athlete.categoria}
                      </span>
                      <AbsenceSummary
                        absentDates={athleteAbsences(appelloMonth, athlete.id)}
                        month={appelloMonth}
                        darkMode={darkMode}
                      />
                    </div>
                    <button
                      onClick={() => toggleAttendance(athlete.id)}
                      disabled={!selectedDay}
                      className={`px-3 py-2 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 ${
                        todayAttendance[athlete.id] === true
                          ? 'bg-green-600 text-white'
                          : darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    >
                      <Check size={20} />
                      Presente
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Athletes Tab */}
        {activeTab === 'athletes' && (
          <div>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <button
                onClick={() => setEditingAthlete({})}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
              >
                <Plus size={20} />
                Aggiungi Atleta
              </button>
              <label
                className={`flex-1 cursor-pointer px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium ${
                  darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'
                } ${importing ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Upload size={20} />
                {importing ? 'Importazione...' : 'Importa da Excel'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={importing}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    importAthletesFile(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
              </label>
              <button
                onClick={downloadAthletesTemplate}
                disabled={exporting}
                className={`flex-1 px-4 py-3 rounded-lg flex items-center gap-2 justify-center font-medium text-white ${
                  darkMode ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-emerald-600 hover:bg-emerald-700'
                } ${exporting ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <Download size={20} />
                {exporting ? 'Download...' : 'Scarica modello Excel'}
              </button>
            </div>

            {importResult && (
              <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <p className="font-semibold mb-1">Risultato importazione</p>
                <p className="text-sm">Importati: {importResult.imported} — Aggiornati: {importResult.updated ?? 0} — Saltati: {importResult.skipped}</p>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {importResult.errors.map((e, idx) => (
                      <p key={idx} className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                        Riga {e.row}: {e.reason}
                      </p>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setImportResult(null)}
                  className={`mt-3 px-3 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                >
                  Chiudi
                </button>
              </div>
            )}

            <div className="space-y-3">
              {athletes.map(athlete => (
                <div key={athlete.id} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold">{athlete.nome} {athlete.cognome}</p>
                      {athlete.email && <p className="text-sm opacity-60">{athlete.email}</p>}
                      {athlete.telefono && <p className="text-sm opacity-60">{athlete.telefono}</p>}
                      {athlete.data_nascita && (
                        <p className="text-sm opacity-60">
                          Nato/a il {new Date(athlete.data_nascita).toLocaleDateString('it-IT')}
                        </p>
                      )}
                      <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                        {athlete.categoria}
                      </span>
                      {athlete.taglia && (
                        <span className={`inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
                          Taglia {athlete.taglia}
                        </span>
                      )}
                      {(athlete.impianti || []).map(nome => (
                        <span key={nome} className={`inline-block mt-1 ml-1 text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                          {nome}
                        </span>
                      ))}
                      {(athlete.codice_tessera_fin || athlete.id_fin) && (
                        <p className="mt-1 text-xs opacity-60">
                          {athlete.codice_tessera_fin && <span>Tessera FIN: {athlete.codice_tessera_fin}</span>}
                          {athlete.codice_tessera_fin && athlete.id_fin && <span> · </span>}
                          {athlete.id_fin && <span>ID FIN: {athlete.id_fin}</span>}
                        </p>
                      )}
                      <AbsenceSummary
                        absentDates={athleteAbsences(currentMonth, athlete.id)}
                        month={currentMonth}
                        darkMode={darkMode}
                      />
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => setEditingAthlete(athlete)}
                        className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-gray-300 hover:bg-gray-400'}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => deleteAthlete(athlete.id)}
                        className="px-3 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'stats' && (
          <div>
            <div className="mb-4">
              <label className="block text-xs opacity-60 mb-1">Mese</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-200 border-gray-300'}`}
              />
            </div>

            {loading ? (
              <p className="text-center py-8">Caricamento...</p>
            ) : statistics ? (
              <div className="space-y-4">
                {statsSummary && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <p className="text-xs uppercase tracking-wide opacity-60">Media gruppo</p>
                      <p className="text-2xl font-bold mt-1">{statsSummary.averagePresence}%</p>
                    </div>
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <p className="text-xs uppercase tracking-wide opacity-60">Presenze totali</p>
                      <p className="text-2xl font-bold mt-1">{statsSummary.totalPresences}/{statsSummary.totalSessions}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <p className="text-xs uppercase tracking-wide opacity-60">Atleti attivi</p>
                      <p className="text-2xl font-bold mt-1">{statsSummary.athletesPresent}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <p className="text-xs uppercase tracking-wide opacity-60">Assenze</p>
                      <p className="text-2xl font-bold mt-1">{statsSummary.totalAbsences}</p>
                    </div>
                  </div>
                )}

                <div className={`p-4 rounded-lg text-center ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                  <p className="text-3xl font-bold">{statistics.athletesPresentCount}</p>
                  <p className="text-sm opacity-60">Atleti presenti nel mese</p>
                </div>

                {statsSummary && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <p className="text-xs opacity-60">Copertura complessiva</p>
                      <p className="text-xl font-bold mt-1">{statsSummary.attendanceRate}%</p>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <p className="text-xs opacity-60">Oltre 80%</p>
                      <p className="text-xl font-bold mt-1">{statsSummary.aboveTarget}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <p className="text-xs opacity-60">Sotto 50%</p>
                      <p className="text-xl font-bold mt-1">{statsSummary.belowHalf}</p>
                    </div>
                  </div>
                )}

                {statistics.athletes.map(stat => (
                  <div key={stat.athlete_id} className={`p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <p className="font-semibold">{stat.athlete_name}</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p>
                        Presenze: {stat.present_days}/{stat.total_days}
                        {stat.remaining_days > 0 && (
                          <span className="text-xs opacity-60"> · {stat.remaining_days} ancora da svolgere</span>
                        )}
                      </p>
                      <div className="w-full bg-gray-600 rounded-full h-2 mt-1">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${stat.percentage || 0}%` }}
                        />
                      </div>
                      <p className="text-xs opacity-60">
                        {stat.percentage != null ? `${stat.percentage}%` : 'Nessuna presenza questo mese'}
                      </p>
                      {(() => {
                        const categoria = athletes.find(a => a.id === stat.athlete_id)?.categoria;
                        const categoryDays = categories.find(c => c.nome === categoria)?.giorni;
                        const byDay = absencesByWeekday(stat.absent_dates, categoryDays, courseDays.map(d => d.nome));
                        if (byDay.length === 0) return null;
                        return (
                          <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            <span className="text-xs opacity-60">Assenze per giorno:</span>
                            {byDay.map(({ day, count }) => (
                              <span
                                key={day}
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  count > 0
                                    ? (darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700')
                                    : (darkMode ? 'bg-slate-600 text-slate-300' : 'bg-gray-200 text-gray-600')
                                }`}
                              >
                                {day.charAt(0).toUpperCase() + day.slice(1)}: {count}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                      {stat.absent_dates && stat.absent_dates.length > 0 && (
                        <p className="text-xs opacity-60">
                          Assenze: {stat.absent_dates
                            .map(a => new Date(`${a.date}T00:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }))
                            .join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8">Nessuna statistica disponibile</p>
            )}
          </div>
        )}

        {/* Races Tab: manifestazioni e tempi (tipologie e manifestazioni si creano dal pannello Admin) */}
        {activeTab === 'races' && (
          <RacesTab athletes={athletes} appelloAthletes={appelloAthletes} darkMode={darkMode} />
        )}

      </div>

      {/* Logout Button */}
      <div className="fixed bottom-6 left-4 right-4 max-w-4xl mx-auto">
        <button
          onClick={logout}
          className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
        >
          <LogOut size={20} />
          Logout
        </button>
      </div>

      {/* Athlete Add/Edit Modal */}
      {editingAthlete !== null && (
        <AthleteFormModal
          athlete={editingAthlete}
          categories={categories}
          impianti={impianti}
          defaultImpianto={effectiveImpianto || allowedImpianti[0] || ''}
          onSave={saveAthlete}
          onCancel={() => setEditingAthlete(null)}
          darkMode={darkMode}
        />
      )}

    </div>
  );
}

function LoginScreen({ onGoogleLogin, googleClientId, darkMode, setDarkMode }) {
  const [backendStatus, setBackendStatus] = useState('checking');
  const googleButtonRef = useRef(null);

  // Inizializza e disegna il pulsante "Accedi con Google" (Google Identity Services,
  // caricato via script async in public/index.html) solo se è stato configurato un client ID.
  // Lo script può non essere ancora pronto al primo render: si ritenta finché non lo è.
  useEffect(() => {
    if (!googleClientId) return;

    let cancelled = false;
    const tryRender = () => {
      if (cancelled || !googleButtonRef.current) return;
      if (!window.google?.accounts?.id) {
        setTimeout(tryRender, 200);
        return;
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => onGoogleLogin(response.credential)
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: darkMode ? 'filled_black' : 'outline',
        size: 'large',
        width: 320,
        locale: 'it'
      });
    };

    tryRender();
    return () => { cancelled = true; };
  }, [googleClientId, darkMode, onGoogleLogin]);

  useEffect(() => {
    let cancelled = false;

    const checkHealth = async () => {
      try {
        const response = await fetch(`${API_URL}/health`);
        let body = null;
        try {
          body = await response.json();
        } catch (parseErr) {
          body = null;
        }
        if (cancelled) return;
        if (response.ok && body?.database === 'connected') {
          setBackendStatus('ok');
        } else {
          setBackendStatus('db-down');
        }
      } catch (err) {
        if (!cancelled) setBackendStatus('unreachable');
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const statusConfig = {
    checking: {
      icon: <Clock size={16} className={darkMode ? 'text-slate-400' : 'text-gray-500'} />,
      text: 'Verifica connessione al server…',
      className: darkMode ? 'text-slate-400' : 'text-gray-500',
    },
    ok: {
      icon: <CheckCircle size={16} className="text-green-500" />,
      text: 'Server connesso',
      className: darkMode ? 'text-green-400' : 'text-green-600',
    },
    unreachable: {
      icon: <WifiOff size={16} className="text-red-500" />,
      text: 'Impossibile raggiungere il server',
      className: darkMode ? 'text-red-400' : 'text-red-600',
    },
    'db-down': {
      icon: <AlertCircle size={16} className="text-red-500" />,
      text: 'Server raggiungibile ma database non connesso',
      className: darkMode ? 'text-red-400' : 'text-red-600',
    },
  };

  const status = statusConfig[backendStatus];
  const loginDisabled = backendStatus === 'unreachable';

  return (
    <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-slate-900' : 'bg-gray-50'} px-4`}>
      <div className={`w-full max-w-md p-8 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">🏊</h1>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Appello Nuoto</h2>
          <p className={`text-sm mt-2 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>Gestione presenze atleti</p>
          <div className={`flex items-center justify-center gap-2 mt-3 text-xs ${status.className}`}>
            {status.icon}
            <span>{status.text}</span>
          </div>
        </div>

        {(backendStatus === 'unreachable' || backendStatus === 'db-down') && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-start gap-2 ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              {backendStatus === 'unreachable'
                ? 'Il server non risponde. Controlla la connessione o riprova più tardi.'
                : 'Il server è raggiungibile ma non riesce a connettersi al database. Riprova più tardi.'}
            </span>
          </div>
        )}

        {googleClientId ? (
          <>
            <div className={`flex justify-center ${loginDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <div ref={googleButtonRef} />
            </div>
            <p className={`mt-3 text-center text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Il primo accesso con Google registra automaticamente il tuo account, se la tua email è abilitata
            </p>
          </>
        ) : (
          <p className={`text-center text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Login con Google non configurato su questo ambiente. Contatta l'amministratore.
          </p>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
          >
            {darkMode ? 'Light' : 'Dark'} Mode
          </button>
        </div>

        <p className={`mt-4 text-center text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
          {APP_VERSION_LABEL}
        </p>
      </div>
    </div>
  );
}

// Etichette degli stati di un tempo (Result.stato) usati dal backend.
const STATI_TEMPO = { SQ: 'Squalificato', RT: 'Ritirato', NP: 'Non partito' };

// Formatta una data "YYYY-MM-DD" (o ISO) in it-IT senza slittamenti di fuso orario.
const formatDataGara = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return String(value);
  return new Date(y, m - 1, d).toLocaleDateString('it-IT');
};

// Distacco di un tempo dal personal best, in centesimi: "+1,23" sotto il minuto, "+1:02,45" oltre.
const formatDistacco = (cs) => {
  const n = Math.max(0, Math.round(Number(cs) || 0));
  const min = Math.floor(n / 6000);
  const sec = Math.floor((n % 6000) / 100);
  const cent = String(n % 100).padStart(2, '0');
  return min > 0 ? `+${min}:${String(sec).padStart(2, '0')},${cent}` : `+${sec},${cent}`;
};

// Storico tempi e personal best di un atleta (GET /api/athletes/:id/results).
function AthleteResultsView({ athletes, darkMode }) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Personal best con l'elenco degli altri tempi aperto (chiave "race_type_id|vasca") e riga dello
  // storico evidenziata dopo il clic su uno di quei tempi (`n` fa ripartire l'evidenziazione anche
  // se si clicca di nuovo lo stesso tempo).
  const [expandedPb, setExpandedPb] = useState({});
  const [highlight, setHighlight] = useState(null);
  const historyRowRefs = useRef(new Map());

  const sortedAthletes = [...(athletes || [])].sort((a, b) =>
    `${a.cognome || ''} ${a.nome || ''}`.localeCompare(`${b.cognome || ''} ${b.nome || ''}`, 'it', { sensitivity: 'base' })
  );
  const query = search.trim().toLowerCase();
  const filteredAthletes = query
    ? sortedAthletes.filter(a =>
        `${a.cognome || ''} ${a.nome || ''} ${a.nome || ''} ${a.cognome || ''}`.toLowerCase().includes(query)
        || String(a.id) === selectedId
      )
    : sortedAthletes;

  useEffect(() => {
    setExpandedPb({});
    setHighlight(null);
    if (!selectedId) {
      setResults(null);
      setError('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setResults(null);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/athletes/${selectedId}/results`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok) {
          setResults({
            history: Array.isArray(data.history) ? data.history : [],
            personal_bests: Array.isArray(data.personal_bests) ? data.personal_bests : []
          });
        } else {
          setError(data.error || response.statusText || 'Errore nel caricamento dei tempi');
        }
      } catch (err) {
        if (!cancelled) setError('Errore nel caricamento dei tempi: ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedId]);

  useEffect(() => {
    if (!highlight) return undefined;
    const timer = setTimeout(() => setHighlight(null), 2000);
    return () => clearTimeout(timer);
  }, [highlight]);

  // Porta in vista la riga dello storico di un tempo e la evidenzia per qualche istante.
  const showInHistory = (resultId) => {
    const row = historyRowRefs.current.get(resultId);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlight({ id: resultId, n: Date.now() });
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;
  const cardClass = `p-4 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`;
  const mutedClass = darkMode ? 'text-slate-400' : 'text-gray-600';
  const timeClass = 'font-mono text-lg font-semibold tabular-nums';

  const pbByVasca = [25, 50]
    .map(vasca => ({
      vasca,
      items: (results?.personal_bests || []).filter(r => Number(r.vasca) === vasca)
    }))
    .filter(group => group.items.length > 0);

  // Tempi individuali validi (stesso filtro del backend per i personal best: niente staffette, niente
  // stato, tempo presente) raggruppati per gara e vasca, dal più veloce: 25 e 50 m restano separati.
  const validTimesByEvent = new Map();
  for (const r of results?.history || []) {
    if (r.staffetta || r.stato || r.tempo_centesimi == null) continue;
    const key = `${r.race_type_id}|${r.vasca}`;
    if (!validTimesByEvent.has(key)) validTimesByEvent.set(key, []);
    validTimesByEvent.get(key).push(r);
  }
  for (const times of validTimesByEvent.values()) {
    times.sort((a, b) => a.tempo_centesimi - b.tempo_centesimi || String(b.data).localeCompare(String(a.data)));
  }
  const otherTimesOf = (pb) =>
    (validTimesByEvent.get(`${pb.race_type_id}|${pb.vasca}`) || []).filter(r => r.id !== pb.id);

  const hasResults = results && (results.history.length > 0 || results.personal_bests.length > 0);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-xs opacity-60">Atleta</label>
        <input
          type="text"
          placeholder="Cerca per nome o cognome"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={inputClass}
        />
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className={inputClass}
        >
          <option value="">— Seleziona un atleta —</option>
          {filteredAthletes.map(a => (
            <option key={a.id} value={String(a.id)}>{a.cognome} {a.nome}</option>
          ))}
        </select>
        {query && filteredAthletes.length === 0 && (
          <p className={`text-sm ${mutedClass}`}>Nessun atleta corrisponde alla ricerca.</p>
        )}
      </div>

      {!selectedId && (
        <p className={`text-sm ${mutedClass}`}>Seleziona un atleta per vedere personal best e storico dei tempi.</p>
      )}

      {loading && (
        <p className={`text-sm ${mutedClass}`}>Caricamento tempi...</p>
      )}

      {error && (
        <div className={`p-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
          {error}
        </div>
      )}

      {selectedId && !loading && !error && results && !hasResults && (
        <p className={`text-sm ${mutedClass}`}>Nessun tempo registrato per questo atleta</p>
      )}

      {hasResults && (
        <>
          <div>
            <h3 className="text-lg font-bold mb-2">Personal best</h3>
            {pbByVasca.length === 0 ? (
              <p className={`text-sm ${mutedClass}`}>Nessun tempo individuale valido.</p>
            ) : (
              <div className="space-y-4">
                {pbByVasca.map(group => (
                  <div key={group.vasca}>
                    <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-sky-300' : 'text-sky-700'}`}>
                      Vasca {group.vasca} m
                    </p>
                    <div className="space-y-2">
                      {group.items.map(pb => {
                        const eventKey = `${pb.race_type_id}|${pb.vasca}`;
                        const others = otherTimesOf(pb);
                        const expanded = Boolean(expandedPb[eventKey]);
                        return (
                          <div key={pb.id} className={cardClass}>
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold">{pb.gara}</p>
                                <p className={`text-xs ${mutedClass}`}>
                                  {pb.competition_nome}{pb.data ? ` · ${formatDataGara(pb.data)}` : ''}
                                </p>
                              </div>
                              <span className={`${timeClass} shrink-0`}>{pb.tempo}</span>
                            </div>
                            {others.length > 0 && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setExpandedPb(prev => ({ ...prev, [eventKey]: !prev[eventKey] }))}
                                  aria-expanded={expanded}
                                  className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${darkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'}`}
                                >
                                  {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  {expanded
                                    ? 'Nascondi altri tempi'
                                    : `+${others.length} ${others.length === 1 ? 'altro tempo' : 'altri tempi'}`}
                                </button>
                                {expanded && (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {others.map(r => (
                                      <button
                                        key={r.id}
                                        type="button"
                                        onClick={() => showInHistory(r.id)}
                                        title="Mostra nello storico"
                                        className={`max-w-full text-left px-3 py-1.5 rounded-lg border transition ${darkMode ? 'bg-slate-800 border-slate-600 hover:border-sky-400' : 'bg-white border-gray-300 hover:border-sky-500'}`}
                                      >
                                        <span className="font-mono font-semibold tabular-nums">{r.tempo}</span>
                                        <span className={`ml-2 text-xs font-mono tabular-nums ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                          {formatDistacco(r.tempo_centesimi - pb.tempo_centesimi)}
                                        </span>
                                        <span className={`block text-xs truncate ${mutedClass}`}>
                                          {r.competition_nome}{r.data ? ` · ${formatDataGara(r.data)}` : ''}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-lg font-bold mb-2">Storico</h3>
            {results.history.length === 0 ? (
              <p className={`text-sm ${mutedClass}`}>Nessun tempo registrato per questo atleta</p>
            ) : (
              <div className="space-y-2">
                {results.history.map(r => (
                  <div
                    key={`${r.id}-${r.frazione ?? ''}`}
                    ref={r.staffetta ? undefined : (el) => {
                      if (el) historyRowRefs.current.set(r.id, el);
                      else historyRowRefs.current.delete(r.id);
                    }}
                    className={`${cardClass} flex items-start justify-between gap-3 transition-shadow ${highlight && highlight.id === r.id && !r.staffetta ? 'ring-2 ring-amber-400' : ''}`}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold">
                        {r.gara}
                        <span className={`ml-2 text-xs font-normal px-2 py-0.5 rounded-full ${darkMode ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
                          Vasca {r.vasca} m
                        </span>
                      </p>
                      <p className={`text-xs ${mutedClass}`}>
                        {formatDataGara(r.data)} · {r.competition_nome}
                      </p>
                      {r.staffetta && (
                        <p className={`text-xs ${mutedClass}`}>
                          {r.squadra ? `${r.squadra} · ` : ''}{r.frazione ? `frazione ${r.frazione}` : 'staffetta'}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      {r.stato ? (
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          {STATI_TEMPO[r.stato] || r.stato}
                        </span>
                      ) : (
                        <span className={timeClass}>{r.tempo}</span>
                      )}
                      {r.posizione != null && r.posizione !== '' && (
                        <p className={`text-xs ${mutedClass}`}>{r.posizione}°</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AthleteFormModal({ athlete, categories, impianti = [], defaultImpianto = '', onSave, onCancel, darkMode }) {
  const isEditing = Boolean(athlete.id);
  const [nome, setNome] = useState(athlete.nome || '');
  const [cognome, setCognome] = useState(athlete.cognome || '');
  const [dataNascita, setDataNascita] = useState(
    athlete.data_nascita ? athlete.data_nascita.split('T')[0] : ''
  );
  const [email, setEmail] = useState(athlete.email || '');
  const [telefono, setTelefono] = useState(athlete.telefono || '');
  const [categoria, setCategoria] = useState(athlete.categoria || categories[0]?.nome || '');
  const [taglia, setTaglia] = useState(athlete.taglia || '');
  const [codiceTesseraFin, setCodiceTesseraFin] = useState(athlete.codice_tessera_fin || '');
  const [idFin, setIdFin] = useState(athlete.id_fin || '');
  // Di norma un solo impianto; un nuovo atleta parte dall'impianto dell'appello (o da Pordenone)
  const [athleteImpianti, setAthleteImpianti] = useState(
    athlete.impianti && athlete.impianti.length > 0
      ? athlete.impianti
      : [defaultImpianto || impianti.find(i => i.nome === 'Pordenone')?.nome || impianti[0]?.nome].filter(Boolean)
  );

  const toggleImpianto = (nome) => {
    setAthleteImpianti(prev => prev.includes(nome) ? prev.filter(n => n !== nome) : [...prev, nome]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim() || !cognome.trim()) return;
    if (impianti.length > 0 && athleteImpianti.length === 0) {
      alert('Seleziona almeno un impianto');
      return;
    }

    onSave({
      ...(isEditing ? { id: athlete.id } : {}),
      nome: nome.trim(),
      cognome: cognome.trim(),
      data_nascita: dataNascita || null,
      email: email.trim() || null,
      telefono: telefono.trim() || null,
      categoria,
      taglia: taglia || null,
      codice_tessera_fin: codiceTesseraFin.trim() || null,
      id_fin: idFin.trim() || null,
      impianti: athleteImpianti
    });
  };

  const inputClass = `w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center px-4 z-50">
      <div className={`w-full max-w-md max-h-[90vh] overflow-y-auto p-6 rounded-xl shadow-lg ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <h3 className="text-xl font-bold mb-4">
          {isEditing ? 'Modifica Atleta' : 'Aggiungi Atleta'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            className={inputClass}
          />
          <input
            type="text"
            placeholder="Cognome"
            value={cognome}
            onChange={(e) => setCognome(e.target.value)}
            required
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Data di nascita</label>
            <input
              type="date"
              value={dataNascita}
              onChange={(e) => setDataNascita(e.target.value)}
              className={inputClass}
            />
          </div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
          <input
            type="tel"
            placeholder="Numero di telefono"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            className={inputClass}
          />
          <div>
            <label className="block text-xs opacity-60 mb-1">Categoria</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className={inputClass}
            >
              {categories.map(c => (
                <option key={c.id} value={c.nome}>{c.nome}</option>
              ))}
            </select>
          </div>
          {impianti.length > 0 && (
            <div>
              <label className="block text-xs opacity-60 mb-1">Impianti (di norma uno solo)</label>
              <div className="flex flex-wrap gap-2">
                {impianti.map(i => (
                  <label key={i.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer ${darkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                    <input
                      type="checkbox"
                      checked={athleteImpianti.includes(i.nome)}
                      onChange={() => toggleImpianto(i.nome)}
                    />
                    {i.nome}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs opacity-60 mb-1">Taglia maglietta</label>
            <select
              value={taglia}
              onChange={(e) => setTaglia(e.target.value)}
              className={inputClass}
            >
              <option value="">— Non indicata —</option>
              {TAGLIE.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs opacity-60 mb-1">Codice tessera FIN</label>
            <input
              type="text"
              placeholder="Facoltativo"
              value={codiceTesseraFin}
              onChange={(e) => setCodiceTesseraFin(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs opacity-60 mb-1">ID FIN</label>
            <input
              type="text"
              placeholder="Facoltativo"
              value={idFin}
              onChange={(e) => setIdFin(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, darkMode }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-medium transition border-b-2 ${
        active
          ? `border-blue-500 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`
          : `border-transparent opacity-60 hover:opacity-80`
      }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// ========== TAB GARE ==========
// Manifestazioni e tipologie di gara si creano solo dal pannello Admin: qui l'istruttore consulta la
// manifestazione e inserisce i tempi (individuali e staffette), oppure consulta lo storico di un atleta.

async function racesApi(path, options = {}) {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...(options.body ? { 'Content-Type': 'application/json' } : {})
    }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || response.statusText || 'Errore di comunicazione con il server');
  }
  return data;
}

function competitionLabel(c) {
  const data = c.data ? new Date(`${String(c.data).slice(0, 10)}T00:00:00`).toLocaleDateString('it-IT') : '';
  return [data, c.nome, c.luogo, c.vasca ? `vasca ${c.vasca} m` : ''].filter(Boolean).join(' — ');
}

function sortByCognome(list) {
  return [...list].sort((a, b) =>
    `${a.cognome || ''} ${a.nome || ''}`.localeCompare(`${b.cognome || ''} ${b.nome || ''}`, 'it', { sensitivity: 'base' })
  );
}

function resultLabel(r) {
  if (r.stato) return r.tempo ? `${r.tempo} (${r.stato})` : `${r.stato} — ${STATI_GARA[r.stato] || ''}`;
  return r.tempo || formatTempo(r.tempo_centesimi) || '—';
}

// Valida i campi comuni (tempo/stato/posizione) e costruisce la parte comune del body.
function buildResultFields({ tempo, posizione, stato }) {
  const t = tempo.trim();
  if (!t && !stato) return { error: 'Inserisci un tempo oppure uno stato (SQ / RT / NP)' };
  if (t && !isValidTempo(t)) return { error: 'Formato tempo non valido: usa MM:SS,cc (es. 01:05,32)' };
  const pos = String(posizione).trim();
  if (pos && (!/^\d+$/.test(pos) || Number(pos) < 1)) return { error: 'La posizione deve essere un numero intero positivo' };
  return { fields: { tempo: t, posizione: pos ? Number(pos) : null, stato: stato || null } };
}

function RacesTab({ athletes, appelloAthletes, darkMode }) {
  const [mode, setMode] = useState('insert'); // insert | athlete
  const [raceTypes, setRaceTypes] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [competitionsLoaded, setCompetitionsLoaded] = useState(false);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedRaceId, setSelectedRaceId] = useState(null);
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');

  const inputCls = `px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-200 border-gray-300'}`;
  const cardCls = `${darkMode ? 'bg-slate-800' : 'bg-white'} rounded-lg p-3 shadow`;

  const fetchRaceTypes = async () => {
    try {
      setRaceTypes(await racesApi('/api/race-types'));
    } catch (err) {
      console.error('Failed to fetch race types:', err);
    }
  };

  const fetchCompetitions = async () => {
    try {
      const data = await racesApi('/api/competitions');
      setCompetitions(data);
      setSelectedCompetitionId(prev =>
        prev && data.some(c => String(c.id) === String(prev)) ? prev : (data[0] ? String(data[0].id) : '')
      );
      setLoadError('');
    } catch (err) {
      setLoadError('Errore caricamento manifestazioni: ' + err.message);
    } finally {
      setCompetitionsLoaded(true);
    }
  };

  const fetchDetail = async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      setLoadingDetail(true);
      const data = await racesApi(`/api/competitions/${id}`);
      setDetail(data);
      setSelectedRaceId(prev => (prev && (data.gare || []).some(g => g.id === prev) ? prev : null));
      setLoadError('');
    } catch (err) {
      setLoadError('Errore caricamento manifestazione: ' + err.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Tipologie e manifestazioni si caricano all'apertura della tab (il componente è montato solo lì)
  useEffect(() => {
    fetchRaceTypes();
    fetchCompetitions();
  }, []);

  useEffect(() => {
    fetchDetail(selectedCompetitionId);
  }, [selectedCompetitionId]);

  // Salvataggio: POST se il risultato non esiste, PUT se esiste. Lancia l'errore (messaggio del backend)
  // così il chiamante lo mostra accanto al form; dopo ogni salvataggio ricarica il dettaglio.
  const saveResult = async (existingId, body) => {
    if (existingId) {
      const { race_type_id, ...rest } = body;
      await racesApi(`/api/results/${existingId}`, { method: 'PUT', body: JSON.stringify(rest) });
    } else {
      await racesApi(`/api/competitions/${selectedCompetitionId}/results`, { method: 'POST', body: JSON.stringify(body) });
    }
    await fetchDetail(selectedCompetitionId);
  };

  const deleteResult = async (resultId) => {
    if (!window.confirm('Eliminare questo tempo?')) return;
    try {
      await racesApi(`/api/results/${resultId}`, { method: 'DELETE' });
      await fetchDetail(selectedCompetitionId);
    } catch (err) {
      alert('Errore eliminazione tempo: ' + err.message);
    }
  };

  const gare = detail?.gare?.length
    ? detail.gare
    : (detail ? raceTypes.filter(rt => (competitions.find(c => String(c.id) === String(detail.id))?.gare || []).includes(rt.id)) : []);
  const results = detail?.results || [];
  const countByRace = results.reduce((acc, r) => ({ ...acc, [r.race_type_id]: (acc[r.race_type_id] || 0) + 1 }), {});
  const selectedRace = gare.find(g => g.id === selectedRaceId) || null;
  const raceResults = selectedRace ? results.filter(r => r.race_type_id === selectedRace.id) : [];

  const toggleCls = (active) => `flex-1 py-2 rounded-lg text-sm font-medium transition ${
    active ? 'bg-blue-600 text-white' : (darkMode ? 'text-slate-300' : 'text-slate-600')
  }`;

  const raceChip = (g) => {
    const active = g.id === selectedRaceId;
    const count = countByRace[g.id] || 0;
    return (
      <button
        key={g.id}
        onClick={() => setSelectedRaceId(active ? null : g.id)}
        className={`px-3 py-2 rounded-lg text-sm font-medium border transition flex items-center gap-2 ${
          active
            ? 'bg-blue-600 border-blue-600 text-white'
            : (darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-300')
        }`}
      >
        <span>{g.nome || g.codice}</span>
        <span className={`text-xs px-1.5 rounded-full ${
          count > 0
            ? (active ? 'bg-white/25' : (darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'))
            : (active ? 'bg-white/25' : 'opacity-60')
        }`}>
          {count}
        </span>
      </button>
    );
  };

  // Lista atleti per la gara individuale: quelli visibili in Appello (categorie dell'istruttore) più
  // chiunque abbia già un tempo in questa gara; chi ha un tempo sta in cima, poi gli altri per cognome.
  const resultByAthlete = {};
  if (selectedRace && !selectedRace.staffetta) {
    raceResults.forEach(r => {
      const a = r.atleti && r.atleti[0];
      if (a) resultByAthlete[a.athlete_id] = r;
    });
  }
  const individualAthletes = (() => {
    if (!selectedRace || selectedRace.staffetta) return [];
    const ids = new Set(appelloAthletes.map(a => a.id));
    const base = [...appelloAthletes, ...athletes.filter(a => !ids.has(a.id) && resultByAthlete[a.id])];
    const q = search.trim().toLowerCase();
    const filtered = q ? base.filter(a => `${a.nome} ${a.cognome}`.toLowerCase().includes(q) || `${a.cognome} ${a.nome}`.toLowerCase().includes(q)) : base;
    const sorted = sortByCognome(filtered);
    return [...sorted.filter(a => resultByAthlete[a.id]), ...sorted.filter(a => !resultByAthlete[a.id])];
  })();

  return (
    <div className="space-y-4">
      <div className={`flex gap-1 p-1 rounded-lg ${darkMode ? 'bg-slate-800' : 'bg-gray-200'}`}>
        <button onClick={() => setMode('insert')} className={toggleCls(mode === 'insert')}>Inserisci tempi</button>
        <button onClick={() => setMode('athlete')} className={toggleCls(mode === 'athlete')}>Tempi atleta</button>
      </div>

      {mode === 'athlete' && (
        <AthleteResultsView athletes={athletes} darkMode={darkMode} />
      )}

      {mode === 'insert' && (
        <div className="space-y-4">
          {loadError && (
            <div className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
              <AlertCircle size={16} /> {loadError}
            </div>
          )}

          {competitionsLoaded && competitions.length === 0 ? (
            <div className={`${cardCls} text-center py-8 text-sm opacity-80`}>
              Nessuna manifestazione: chiedi a un amministratore di crearla dal pannello Admin
            </div>
          ) : (
            <div>
              <label className="block text-xs opacity-60 mb-1">Manifestazione</label>
              <select
                value={selectedCompetitionId}
                onChange={(e) => setSelectedCompetitionId(e.target.value)}
                className={`w-full ${inputCls} py-3`}
              >
                {competitions.map(c => (
                  <option key={c.id} value={String(c.id)}>{competitionLabel(c)}</option>
                ))}
              </select>
            </div>
          )}

          {detail && (
            <div className={cardCls}>
              <div className="flex items-start gap-2">
                <Trophy size={20} className="mt-0.5 text-amber-500 shrink-0" />
                <div className="min-w-0">
                  <h2 className="font-bold text-lg leading-tight">{detail.nome}</h2>
                  <p className="text-sm opacity-70">
                    {[
                      detail.data ? new Date(`${String(detail.data).slice(0, 10)}T00:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '',
                      detail.luogo,
                      detail.vasca ? `vasca ${detail.vasca} m` : '',
                      detail.tipo
                    ].filter(Boolean).join(' · ')}
                  </p>
                  {detail.note && <p className="text-xs opacity-60 mt-1">{detail.note}</p>}
                </div>
              </div>

              {gare.length === 0 ? (
                <p className="text-sm opacity-60 mt-3">Nessuna gara prevista in questa manifestazione.</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {[
                    ['Individuali', gare.filter(g => !g.staffetta)],
                    ['Staffette', gare.filter(g => g.staffetta)]
                  ].filter(([, list]) => list.length > 0).map(([title, list]) => (
                    <div key={title}>
                      <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">{title}</p>
                      <div className="flex flex-wrap gap-2">{list.map(raceChip)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {loadingDetail && <p className="text-center text-sm opacity-60">Caricamento…</p>}

          {selectedRace && !selectedRace.staffetta && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold">{selectedRace.nome}</h3>
                <span className="text-xs opacity-60">{raceResults.length} tempi inseriti</span>
              </div>
              <input
                type="text"
                placeholder="Cerca atleta…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full ${inputCls} py-3`}
              />
              {individualAthletes.length === 0 ? (
                <p className="text-center py-6 text-sm opacity-60">Nessun atleta trovato</p>
              ) : individualAthletes.map(a => {
                const r = resultByAthlete[a.id];
                return (
                  <IndividualResultRow
                    key={`${a.id}-${r ? `${r.id}-${r.tempo}-${r.posizione}-${r.stato}` : 'new'}`}
                    athlete={a}
                    result={r}
                    raceTypeId={selectedRace.id}
                    onSave={saveResult}
                    onDelete={deleteResult}
                    darkMode={darkMode}
                  />
                );
              })}
            </div>
          )}

          {selectedRace && selectedRace.staffetta && (
            <RelaySection
              race={selectedRace}
              results={raceResults}
              athletes={athletes}
              onSave={saveResult}
              onDelete={deleteResult}
              darkMode={darkMode}
            />
          )}
        </div>
      )}
    </div>
  );
}

function StatoSelect({ value, onChange, darkMode, className = '' }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`px-2 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-200 border-gray-300'} ${className}`}
      title="Stato"
    >
      <option value="">—</option>
      {Object.entries(STATI_GARA).map(([k, label]) => (
        <option key={k} value={k}>{k} · {label}</option>
      ))}
    </select>
  );
}

function IndividualResultRow({ athlete, result, raceTypeId, onSave, onDelete, darkMode }) {
  const [tempo, setTempo] = useState(result?.tempo || formatTempo(result?.tempo_centesimi));
  const [posizione, setPosizione] = useState(result?.posizione ?? '');
  const [stato, setStato] = useState(result?.stato || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const inputCls = `px-2 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-200 border-gray-300'}`;

  const save = async () => {
    const { error: validationError, fields } = buildResultFields({ tempo, posizione, stato });
    if (validationError) { setError(validationError); return; }
    try {
      setSaving(true);
      setError('');
      await onSave(result?.id, { race_type_id: raceTypeId, athlete_ids: [athlete.id], ...fields });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-lg p-3 shadow border-l-4 ${darkMode ? 'bg-slate-800' : 'bg-white'} ${
      result ? 'border-green-500' : 'border-transparent'
    }`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="font-medium truncate">{athlete.cognome} {athlete.nome}</p>
        {result && (
          <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'}`}>
            {resultLabel(result)}{result.posizione ? ` · ${result.posizione}°` : ''}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          inputMode="decimal"
          placeholder="MM:SS,cc"
          value={tempo}
          onChange={(e) => setTempo(e.target.value)}
          className={`${inputCls} font-mono w-28`}
        />
        <input
          type="number"
          min="1"
          placeholder="Pos."
          value={posizione}
          onChange={(e) => setPosizione(e.target.value)}
          className={`${inputCls} w-16`}
          title="Posizione"
        />
        <StatoSelect value={stato} onChange={setStato} darkMode={darkMode} />
        <div className="flex gap-2 ml-auto">
          <button
            onClick={save}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-2 rounded-lg flex items-center gap-1 text-sm"
          >
            <Save size={16} /> {saving ? '…' : 'Salva'}
          </button>
          {result && (
            <button
              onClick={() => onDelete(result.id)}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg"
              title="Elimina tempo"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
      {error && <p className={`text-xs mt-2 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{error}</p>}
    </div>
  );
}

function RelaySection({ race, results, athletes, onSave, onDelete, darkMode }) {
  const [editingId, setEditingId] = useState(null); // null = nessuna, 'new' = nuova staffetta, id = modifica
  const sortedResults = [...results].sort((a, b) =>
    (a.posizione ?? Infinity) - (b.posizione ?? Infinity) || (a.tempo_centesimi ?? Infinity) - (b.tempo_centesimi ?? Infinity)
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">{race.nome}</h3>
        <span className="text-xs opacity-60">{results.length} staffette registrate</span>
      </div>
      {race.mistaffetta && (
        <p className={`text-xs px-3 py-2 rounded-lg ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
          Mistaffetta: la squadra deve essere mista M/F (di norma 2 uomini e 2 donne).
        </p>
      )}

      {sortedResults.length === 0 && editingId !== 'new' && (
        <p className="text-center py-4 text-sm opacity-60">Nessuna staffetta registrata per questa gara.</p>
      )}

      {sortedResults.map(r => (
        editingId === r.id ? (
          <RelayForm
            key={r.id}
            race={race}
            result={r}
            athletes={athletes}
            onSave={async (body) => { await onSave(r.id, body); setEditingId(null); }}
            onCancel={() => setEditingId(null)}
            darkMode={darkMode}
          />
        ) : (
          <div key={r.id} className={`rounded-lg p-3 shadow border-l-4 border-green-500 ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium">{r.squadra || 'Staffetta'}</p>
                <p className="text-sm">
                  <span className="font-mono">{resultLabel(r)}</span>
                  {r.posizione ? <span className="opacity-70"> · {r.posizione}° posto</span> : null}
                </p>
                <ol className="text-xs opacity-70 mt-1 space-y-0.5">
                  {[...(r.atleti || [])].sort((a, b) => a.frazione - b.frazione).map(a => (
                    <li key={`${a.frazione}-${a.athlete_id}`}>{a.frazione}. {a.cognome} {a.nome}</li>
                  ))}
                </ol>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setEditingId(r.id)}
                  className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}
                >
                  Modifica
                </button>
                <button
                  onClick={() => onDelete(r.id)}
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg"
                  title="Elimina staffetta"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        )
      ))}

      {editingId === 'new' ? (
        <RelayForm
          race={race}
          result={null}
          athletes={athletes}
          onSave={async (body) => { await onSave(null, body); setEditingId(null); }}
          onCancel={() => setEditingId(null)}
          darkMode={darkMode}
        />
      ) : (
        <button
          onClick={() => setEditingId('new')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 justify-center"
        >
          <Plus size={18} /> Nuova staffetta
        </button>
      )}
    </div>
  );
}

function RelayForm({ race, result, athletes, onSave, onCancel, darkMode }) {
  const initialLegs = [1, 2, 3, 4].map(f => {
    const a = (result?.atleti || []).find(x => x.frazione === f);
    return a ? String(a.athlete_id) : '';
  });
  const [squadra, setSquadra] = useState(result?.squadra || '');
  const [legs, setLegs] = useState(initialLegs);
  const [tempo, setTempo] = useState(result?.tempo || formatTempo(result?.tempo_centesimi));
  const [posizione, setPosizione] = useState(result?.posizione ?? '');
  const [stato, setStato] = useState(result?.stato || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const inputCls = `px-3 py-2 rounded-lg border ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-200 border-gray-300'}`;
  const sortedAthletes = sortByCognome(athletes);

  const save = async (e) => {
    e.preventDefault();
    const filled = legs.filter(Boolean);
    if (filled.length === 0) { setError('Indica almeno un frazionista'); return; }
    const lastFilled = legs.map(Boolean).lastIndexOf(true);
    if (legs.slice(0, lastFilled + 1).some(l => !l)) { setError('Compila le frazioni in ordine, senza lasciare frazioni vuote in mezzo'); return; }
    if (new Set(filled).size !== filled.length) { setError('Lo stesso atleta non può nuotare due frazioni'); return; }
    const { error: validationError, fields } = buildResultFields({ tempo, posizione, stato });
    if (validationError) { setError(validationError); return; }
    try {
      setSaving(true);
      setError('');
      await onSave({
        race_type_id: race.id,
        athlete_ids: filled.map(Number),
        squadra: squadra.trim() || null,
        ...fields
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className={`rounded-lg p-3 shadow space-y-3 border ${darkMode ? 'bg-slate-800 border-blue-700' : 'bg-white border-blue-300'}`}>
      <p className="font-semibold">{result ? 'Modifica staffetta' : 'Nuova staffetta'}</p>
      {race.mistaffetta && (
        <p className="text-xs opacity-70">Ricorda: la mistaffetta deve essere composta da una squadra mista M/F.</p>
      )}
      <div>
        <label className="block text-xs opacity-60 mb-1">Nome squadra (facoltativo)</label>
        <input type="text" value={squadra} onChange={(e) => setSquadra(e.target.value)} className={`w-full ${inputCls}`} placeholder="Es. Squadra A" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {legs.map((value, i) => (
          <div key={i}>
            <label className="block text-xs opacity-60 mb-1">Frazione {i + 1}</label>
            <select
              value={value}
              onChange={(e) => setLegs(legs.map((l, j) => (j === i ? e.target.value : l)))}
              className={`w-full ${inputCls}`}
            >
              <option value="">—</option>
              {sortedAthletes.map(a => (
                <option key={a.id} value={String(a.id)} disabled={legs.includes(String(a.id)) && value !== String(a.id)}>
                  {a.cognome} {a.nome}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-xs opacity-60 mb-1">Tempo</label>
          <input type="text" inputMode="decimal" placeholder="MM:SS,cc" value={tempo} onChange={(e) => setTempo(e.target.value)} className={`${inputCls} font-mono w-28`} />
        </div>
        <div>
          <label className="block text-xs opacity-60 mb-1">Pos.</label>
          <input type="number" min="1" value={posizione} onChange={(e) => setPosizione(e.target.value)} className={`${inputCls} w-16`} />
        </div>
        <div>
          <label className="block text-xs opacity-60 mb-1">Stato</label>
          <StatoSelect value={stato} onChange={setStato} darkMode={darkMode} />
        </div>
      </div>
      {error && <p className={`text-xs ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className={`flex-1 py-2 rounded-lg ${darkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>Annulla</button>
        <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg flex items-center justify-center gap-1">
          <Save size={16} /> {saving ? 'Salvataggio…' : 'Salva'}
        </button>
      </div>
    </form>
  );
}
